<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'ReplyBottomPanel',
  components: { NextButton, FileUpload, VideoCallButton },
  mixins: [inboxMixin],
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'toggleInsertArticle',
    'toggleEditor',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
    'openFollowUpModal',
    'openForwardContactModal',
    'quickReplySelected',
  ],
  setup(props) {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      falhaRespostas: false,
      ALLOWED_FILE_TYPES,
      showQuickReplies: false,
      quickReplySearch: '',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      cannedResponses: 'getCannedResponses',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isEditorDisabled) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      // Use default file types for private notes
      if (this.isOnPrivateNote) {
        return this.ALLOWED_FILE_TYPES;
      }

      let channelType = this.channelType || this.inbox?.channel_type;

      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    filteredCannedResponses() {
      const q = this.quickReplySearch.toLowerCase();
      if (!q) return this.cannedResponses;
      return this.cannedResponses.filter(
        r =>
          r.short_code.toLowerCase().includes(q) ||
          r.content.toLowerCase().includes(q)
      );
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    // O mesmo botão abre e fecha: ele diz `aria-expanded`, e clicar de novo
    // no raio para sumir com o painel é o gesto natural.
    // A carga tem `catch`: sem ele, a rede fora deixava a lista vazia dizendo
    // "nenhuma resposta pronta" — que é mentira para quem tem trinta.
    async openQuickReplies() {
      if (this.showQuickReplies) {
        this.closeQuickReplies();
        return;
      }
      this.showQuickReplies = true;
      this.quickReplySearch = '';
      this.falhaRespostas = false;
      try {
        await this.$store.dispatch('getCannedResponse', { searchKey: '' });
      } catch {
        this.falhaRespostas = true;
      }
    },
    closeQuickReplies() {
      this.showQuickReplies = false;
      this.quickReplySearch = '';
    },
    selectQuickReply(response) {
      this.$emit('quickReplySelected', response.content);
      this.closeQuickReplies();
    },
  },
};
</script>

<template>
  <div
    :class="
      compact
        ? 'flex items-center flex-shrink-0'
        : `relative flex justify-between p-3 ${
            wrapClass
              ? Object.keys(wrapClass)
                  .filter(k => wrapClass[k])
                  .join(' ')
              : ''
          }`
    "
  >
    <!-- Respostas prontas: o flutuante é a peça do produto (`.advup-menu`,
         com item de duas linhas) e a busca é a `.advup-busca`, na versão
         curta de painel estreito. -->
    <Transition name="rbp-sobe">
      <div v-if="showQuickReplies" class="advup-menu rbp-respostas">
        <div class="rbp-respostas__topo">
          <div class="advup-busca advup-busca--curta rbp-respostas__busca">
            <i class="advup-busca__lupa i-lucide-zap" aria-hidden="true" />
            <input
              v-model="quickReplySearch"
              type="text"
              class="advup-busca__campo reset-base"
              :placeholder="$t('QUICK_REPLIES.SEARCH_PLACEHOLDER')"
              :aria-label="$t('QUICK_REPLIES.SEARCH_PLACEHOLDER')"
            />
          </div>
          <button
            type="button"
            class="advup-acao advup-acao--p"
            :aria-label="$t('GENERAL.CLOSE')"
            @click="closeQuickReplies"
          >
            <i class="i-lucide-x" />
          </button>
        </div>
        <div class="rbp-respostas__lista">
          <button
            v-for="(response, i) in filteredCannedResponses"
            :key="response.id"
            type="button"
            class="advup-menu__item advup-menu__item--duplo advup-entra rbp-resposta"
            :style="{ '--i': Math.min(i, 8) }"
            @click="selectQuickReply(response)"
          >
            <span class="rbp-resposta__atalho">/{{ response.short_code }}</span>
            <span class="advup-menu__nota rbp-resposta__texto">
              {{ response.content }}
            </span>
          </button>
          <p v-if="falhaRespostas" class="advup-nota rbp-vazio rbp-vazio--erro">
            {{ $t('QUICK_REPLIES.LOAD_ERROR') }}
          </p>
          <p
            v-else-if="!filteredCannedResponses.length"
            class="advup-nota rbp-vazio"
          >
            {{ $t('QUICK_REPLIES.EMPTY') }}
          </p>
        </div>
      </div>
    </Transition>

    <div class="left-wrap">
      <!-- As três ações usam `.advup-acao--tingida`, a mesma peça do alto da
           conversa e do topo da caixa de resposta: âmbar (carinha — o calor
           da conversa), íris (resposta pronta — atalho, a família da IA) e
           azul (anexo — conteúdo). -->
      <button
        v-if="!isEditorDisabled"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        type="button"
        class="advup-acao advup-acao--tingida advup-acao--t-ambar"
        :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        @click="toggleEmojiPicker"
      >
        <i class="i-ph-smiley-sticker" />
      </button>
      <button
        v-if="!isEditorDisabled"
        v-tooltip.top-end="$t('QUICK_REPLIES.TOOLTIP')"
        type="button"
        class="advup-acao advup-acao--tingida advup-acao--t-iris"
        :class="{ 'advup-acao--aceso': showQuickReplies }"
        :aria-label="$t('QUICK_REPLIES.TOOLTIP')"
        :aria-expanded="showQuickReplies"
        @click="openQuickReplies"
      >
        <i class="i-lucide-zap" />
      </button>
      <FileUpload
        v-if="showAttachButton"
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <span
          class="advup-acao advup-acao--tingida advup-acao--t-azul"
          :aria-label="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        >
          <i class="i-ph-paperclip" />
        </span>
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton && !compact"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
        slate
        faded
        sm
        @click="toggleAudioRecorder"
      />
      <NextButton
        v-if="showEditorToggle"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        icon="i-ph-quotes"
        slate
        faded
        sm
        @click="$emit('toggleEditor')"
      />
      <NextButton
        v-if="showAudioPlayStopButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <NextButton
        v-if="!compact && showQuotedReplyToggle"
        v-tooltip.top-end="quotedReplyToggleTooltip"
        icon="i-ph-quotes"
        :variant="quotedReplyEnabled ? 'solid' : 'faded'"
        color="slate"
        sm
        :aria-pressed="quotedReplyEnabled"
        @click="$emit('toggleQuotedReply')"
      />
      <NextButton
        v-if="enableWhatsAppTemplates"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectWhatsappTemplate')"
      />
      <NextButton
        v-if="enableContentTemplates"
        v-tooltip.top-end="'Content Templates'"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectContentTemplate')"
      />
      <VideoCallButton
        v-if="
          !compact &&
          (isAWebWidgetInbox || isAPIInbox) &&
          !isOnPrivateNote &&
          !isEditorDisabled
        "
        :conversation-id="conversationId"
      />
      <NextButton
        v-if="!compact && !isOnPrivateNote"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.FOLLOW_UP')"
        icon="i-ph-calendar"
        slate
        faded
        sm
        @click="$emit('openFollowUpModal')"
      />
      <NextButton
        v-if="!compact && !isOnPrivateNote && !isEditorDisabled"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.FORWARD_CONTACT')"
        icon="i-ph-address-book"
        slate
        faded
        sm
        @click="$emit('openForwardContactModal')"
      />
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
      <NextButton
        v-if="!compact && enableInsertArticleInReply"
        v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
        icon="i-ph-article-ny-times"
        slate
        faded
        sm
        @click="toggleInsertArticle"
      />
    </div>
    <div v-if="!compact" class="right-wrap">
      <NextButton
        :label="sendButtonText"
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="onSend"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* ── Respostas prontas ────────────────────────────────────────────────
   Abrem ACIMA da caixa, com o flutuante do produto. Era uma caixa com raio,
   sombra e animação próprios (uma cúbica escrita à mão). */
.rbp-respostas {
  position: absolute;
  z-index: 50;
  bottom: 100%;
  inset-inline-start: 0;
  width: min(22rem, calc(100vw - 2rem));
  margin-bottom: var(--esp-2);
  padding: 0;
  overflow: hidden;
}

.rbp-respostas__topo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-2);
  border-bottom: 1px solid rgb(var(--slate-4));
}

.rbp-respostas__busca {
  flex: 1;
}

.rbp-respostas__lista {
  overflow-y: auto;
  max-height: 16rem;
  padding: var(--esp-1);
}

/* O atalho aparece como se digita: com a barra na frente, em íris. */
.rbp-resposta__atalho {
  font-weight: var(--peso-forte);
  color: rgb(var(--iris-11));
}

.rbp-resposta__texto {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.rbp-vazio {
  margin: 0;
  padding: var(--esp-4);
  text-align: center;
}

.rbp-vazio--erro {
  color: rgb(var(--ruby-11));
}

.rbp-sobe-enter-active,
.rbp-sobe-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva);
}

.rbp-sobe-enter-from,
.rbp-sobe-leave-to {
  opacity: 0;
  translate: 0 6px;
}

/* As ações ocupam a mesma altura do campo de uma linha (36px) e centram
   nela: quando o campo cresce, a pílula as mantém no fundo, alinhadas com a
   última linha — como nos mensageiros. */
.left-wrap {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  min-height: 36px;
}

.right-wrap {
  @apply flex;
}

:deep(.file-uploads) {
  label {
    cursor: pointer;
  }

  &:hover .advup-acao--tingida {
    background: var(--acao-fundo);
  }
}
</style>
